/**
 * Barra lateral: marca, navegacion e identidad.
 *
 * En escritorio es una columna fija de 264px. En movil, el mismo componente se
 * monta dentro de un cajon (ver `AppShell`), asi que no asume ancho ni posicion:
 * ocupa el 100% de su contenedor.
 */

import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import {
  IconClose,
  IconHome,
  IconLogout,
  IconSearch,
  IconSettings,
  IconSpinner,
} from '@/components/shell/icons';
import { countMenus, filterMenus, firstMenuForModel } from '@/components/shell/menu-utils';
import { groupByTier, TIER_LABEL } from '@/components/shell/menu-tiers';
import { useMenusQuery } from '@/components/shell/queries';
import { SidebarNav } from '@/components/shell/SidebarNav';
import { esAdministrador } from '@/panel/acceso';
import { userInitials, useSessionStore } from '@/store/session';

interface SidebarProps {
  currentModel: string | null;
  /** Menu por el que se entro (`?menu=`), o `null` si se llego por URL directa. */
  currentMenu: string | null;
  /** Se dispara al navegar; en movil cierra el cajon. */
  onNavigate: () => void;
  /** Presente solo en movil: dibuja el boton de cierre del cajon. */
  onClose?: () => void;
}

export function Sidebar({ currentModel, currentMenu, onNavigate, onClose }: SidebarProps) {
  const { data: menus, isPending, isError, error, refetch } = useMenusQuery();
  const user = useSessionStore((estado) => estado.user);
  const tenant = useSessionStore((estado) => estado.tenant);
  const signOut = useSessionStore((estado) => estado.signOut);
  const [filtro, setFiltro] = useState('');

  // El panel de control se ofrece SOLO a quien puede entrar (superusuario o
  // `base.administrador`). Es la misma comprobacion de interfaz que hace
  // `/panel` antes de montarse —la seguridad la sigue haciendo el backend en
  // cada endpoint—: mostrarle el enlace a un operador seria prometerle una
  // pantalla que lo rebota.
  const puedeAdministrar = esAdministrador(user);

  // El arbol podado. Con doce modulos son 206 nodos: escribir dos letras es mas
  // rapido que desplegar tres niveles, y el resultado se dibuja abierto.
  const visibles = useMemo(() => filterMenus(menus ?? [], filtro), [menus, filtro]);
  const filtrando = filtro.trim() !== '';

  // Cual de todas las entradas se marca activa. Con `?menu=` es exacta; sin el
  // (una URL escrita a mano) se elige la primera del modelo, porque marcar las
  // cuatro entradas de Contactos a la vez es peor que marcar una de mas.
  const menuActivo =
    currentMenu ??
    (currentModel === null || menus === undefined ? null : (firstMenuForModel(menus, currentModel)?.name ?? null));

  return (
    <div className="flex h-full flex-col bg-app-subtle">
      {/* Marca */}
      <div className="flex h-topbar shrink-0 items-center gap-2.5 border-b border-line px-4">
        <NavLink
          to="/"
          onClick={onNavigate}
          className="flex min-w-0 items-center gap-2.5 rounded-md py-1"
          aria-label="Nucleo ERP, ir al inicio"
        >
          <span
            aria-hidden="true"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-base font-bold text-accent-contrast"
          >
            N
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold leading-tight">Nucleo</span>
            <span className="block truncate text-2xs uppercase tracking-wide text-fg-faint">
              {tenant?.name ?? 'ERP'}
            </span>
          </span>
        </NavLink>

        {onClose !== undefined && (
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-md p-2 text-fg-muted hover:bg-fg/5 hover:text-fg lg:hidden"
            aria-label="Cerrar menú"
          >
            <IconClose className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Buscador del arbol. No reemplaza a la paleta (Ctrl+K): la paleta lleva
          a una pantalla, esto filtra el arbol y deja ver donde vive cada cosa. */}
      {menus !== undefined && menus.length > 0 && (
        <div className="shrink-0 border-b border-line px-3 py-2.5">
          <div className="relative">
            <IconSearch
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint"
            />
            <input
              type="search"
              value={filtro}
              onChange={(evento) => {
                setFiltro(evento.target.value);
              }}
              onKeyDown={(evento) => {
                if (evento.key === 'Escape') setFiltro('');
              }}
              placeholder="Filtrar menú…"
              aria-label="Filtrar el menú"
              autoComplete="off"
              spellCheck={false}
              className="h-9 w-full rounded-md border border-line bg-surface pl-8 pr-2 text-sm outline-none transition-colors placeholder:text-fg-faint focus:border-accent focus:ring-2 focus:ring-accent/25"
            />
          </div>
        </div>
      )}

      {/* Navegacion */}
      <nav aria-label="Navegación principal" className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <NavLink
          to="/"
          end
          onClick={onNavigate}
          className={({ isActive }) =>
            [
              'touch-target mb-2 flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
              isActive ? 'bg-accent-soft font-medium text-accent' : 'text-fg-muted hover:bg-fg/5 hover:text-fg',
            ].join(' ')
          }
        >
          <IconHome className="h-[1.125rem] w-[1.125rem] shrink-0" />
          Inicio
        </NavLink>

        {isPending && (
          <p className="flex items-center gap-2 px-2.5 py-2 text-sm text-fg-faint">
            <IconSpinner className="h-4 w-4" />
            Cargando menú…
          </p>
        )}

        {isError && (
          <div className="rounded-md border border-danger/30 bg-danger-soft p-3 text-sm">
            <p className="font-medium">No se pudo cargar el menú.</p>
            <p className="mt-1 text-fg-muted">{error.message}</p>
            <button
              type="button"
              onClick={() => {
                void refetch();
              }}
              className="mt-2 font-medium text-accent underline underline-offset-2"
            >
              Reintentar
            </button>
          </div>
        )}

        {menus !== undefined &&
          (menus.length === 0 ? (
            <p className="px-2.5 py-2 text-sm text-fg-faint">
              Todavía no hay módulos con pantallas habilitadas para tu usuario.
            </p>
          ) : visibles.length === 0 ? (
            <p className="px-2.5 py-2 text-sm text-fg-faint">
              Ninguna pantalla coincide con «{filtro}».
            </p>
          ) : (
            filtrando ? (
              // Filtrando: el árbol llega podado y se dibuja plano y abierto. Los
              // pisos por frecuencia estorbarían acá —quien busca «punto de venta»
              // quiere la coincidencia, no la sección a la que pertenece—.
              <>
                <p className="px-2.5 pb-2 text-2xs uppercase tracking-wide text-fg-faint">
                  {countMenus(visibles)} de {countMenus(menus)} entradas
                </p>
                <SidebarNav
                  nodes={visibles}
                  activeMenu={menuActivo}
                  onNavigate={onNavigate}
                  filtrando
                />
              </>
            ) : (
              // Sin filtro: las áreas se agrupan por frecuencia de uso. «Todos los
              // días» arriba, «Gestión» después y «Configuración» al fondo y en
              // gris —doce áreas dejan de pesar lo mismo—.
              <div className="space-y-4">
                {groupByTier(visibles).map(({ tier, nodes }) => (
                  <div key={tier}>
                    <p
                      className={
                        'px-2.5 pb-1.5 text-2xs font-semibold uppercase tracking-wide ' +
                        (tier === 'config' ? 'text-fg-faint/70' : 'text-fg-faint')
                      }
                    >
                      {TIER_LABEL[tier]}
                    </p>
                    <div className={tier === 'config' ? 'opacity-75' : undefined}>
                      <SidebarNav
                        nodes={nodes}
                        activeMenu={menuActivo}
                        onNavigate={onNavigate}
                        filtrando={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )
          ))}
      </nav>

      {/* Identidad */}
      <div className="shrink-0 border-t border-line p-3">
        {/* El panel de control existia y no se podia llegar: habia que escribir
            «/panel» a mano. Va acá, en el menú del usuario, porque administrar
            el sistema es una cosa de la PERSONA y no un área más del ERP —y
            sólo aparece para quien puede entrar—. */}
        {puedeAdministrar && (
          <NavLink
            to="/panel"
            onClick={onNavigate}
            className="touch-target mb-2 flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-fg-muted transition-colors hover:bg-fg/5 hover:text-fg"
          >
            <IconSettings className="h-[1.125rem] w-[1.125rem] shrink-0" />
            Panel de control
          </NavLink>
        )}

        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-surface-raised text-xs font-semibold text-fg-muted"
          >
            {userInitials(user)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{user?.name ?? '—'}</span>
            <span className="block truncate text-xs text-fg-faint">{user?.email ?? user?.username ?? ''}</span>
            {/* Cambiar la propia contraseña no es administrar a nadie: lo hace
                cualquiera y por eso vive acá, junto a su nombre, y no en el
                panel de control (al que la mayoría ni siquiera entra). */}
            <NavLink
              to="/mi-clave"
              onClick={onNavigate}
              className="mt-0.5 block text-xs text-accent hover:underline"
            >
              Cambiar mi contraseña
            </NavLink>
          </span>
          <button
            type="button"
            onClick={signOut}
            className="rounded-md p-2 text-fg-muted transition-colors hover:bg-fg/5 hover:text-fg"
            aria-label="Cerrar sesión"
            title="Cerrar sesión"
          >
            <IconLogout className="h-[1.125rem] w-[1.125rem]" />
          </button>
        </div>
      </div>
    </div>
  );
}
